import _ from "lodash";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import classes from "./CriteriaRubricAssessment.scss";
import { useI18n } from "Hooks";
import {
  Checkbox,
  Tag,
  Modal,
  IconButton,
  useDSContext,
  Tooltip,
} from "@toddle-edu/ds-web";
import {
  InformationOutlined,
  TableOutlined,
  TickCircleOpenOutlined,
  CaretClosedFilled,
  CaretOpenedFilled,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import {
  getDescriptorModalDetails,
  getRatedScoreMemoized,
  getTotalScoreMemoized,
} from "./Utils.js";
import Accordian from "UIComponents/Accordian";
import CriteriaRatingV2 from "UIComponents/CriteriaRatingV2";
import DescriptorTable from "UIComponents/DescriptorTable";
import DescriptorTableV2 from "UIComponents/DescriptorTableV2";
import HelpButton from "AppComponents/HelpButton";
import AIJustification from "UIComponents/AIJustification";
import SkeletonLoader from "UIComponents/SkeletonLoader";
import update from "immutability-helper";
import { GENERIC_TAGS_INFO } from "modules/Services";
import CriteriaComment from "Projects/components/CriteriaComment";
import { getIsAIGeneratingResponseForTool } from "ClassRoom/modules/ClassRoomSelectors";
import { checkIfDisplaySequenceIsFlipped } from "Projects/modules/ProjectModules";
import { getIsCoreOrSubjectGroupType } from "Projects/config/constants";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { CRITERIA_RUBRIC_FINAL_GRADE } from "Projects/Constants/stringConstants";

const TEST_ID_PREFIX = "AppComponents-CriteriaRubricAssessment";
const TYPE_UNMARKED = "unMarked";
const TYPE_EVALUATION = "evaluationScale";

//This is the width of the Rating Element defined in class - ratingElement
const RATING_ELEMENT_WIDTH = 40;

const styles = {
  accordianContainerStyle: {
    border: "none",
    padding: "0px 0px 4px 0px",
  },
  headerStyle: {
    padding: "20px 0px",
  },
  headerStyleV2: {
    padding: "16px 0px",
  },
  checkboxStyle: {
    padding: "26px 0px",
  },
  inlineInfoIconStyles: {
    verticalAlign: "middle",
  },
  containerStyle: {
    justifyContent: "center",
  },
  leftContainerStyle: {
    flex: 1,
  },
  toolWithJustification: {
    borderBottom: "1px solid var(--border-secondary)",
    paddingBottom: "16px",
  },
  starsTwoFilledIconStyle: {
    color: "var(--interactive-tertiary)",
  },
  modalBodyStyle: {
    padding: "24px",
  },
};

//In this function, We are creating a nested structure
//Which has the root level Criteria only, And
//Updating the children => ids to its nodes
//Example : -
// Input -----> [ {id : 1, parent : null, children : [2]} ,
//                { id: 2, parent : 1, children : null } ,
//                { id: 3, parent : null, children : null }  ]
//
// Output -----> [ {id : 1, parent : null, children : [{ id: 2, parent : 1, children : null }]} ,
//                { id: 3, parent : null, children : null }  ]

const getCriteriaNested = ({ criteria }) => {
  return _.reduce(
    criteria,
    (result, item) => {
      const criterionId = _.get(item, "id", "");

      //when no parent push directly
      if (!item.parent) {
        result.push(item);
      } else {
        const parentIndex = _.findIndex(result, addedCriteria =>
          _.isEqual(addedCriteria.id, _.get(item, "parent", null))
        );

        //When parent of item is in result, Update item's id
        //in parent children with item
        if (parentIndex > -1) {
          const childIndex = _.findIndex(
            _.get(result, `${parentIndex}.children`, []),
            childId => childId == criterionId
          );

          result = update(result, {
            [parentIndex]: {
              children: {
                [childIndex]: {
                  $set: item,
                },
              },
            },
          });
        }
      }

      return result;
    },
    []
  );
};

const getCriteriaNestedMemoized = _.memoize(getCriteriaNested, params =>
  JSON.stringify(params)
);

//This Function returns cannot be assessed text for criterion
const CannotBeAssessedTextComp = props => {
  const { t, width, typeOfScale } = props;
  let style = {};

  if (typeOfScale == TYPE_EVALUATION) {
    style = { width };
  }

  const assessedTextContainerClass = classNames(
    classes.assessedTextContainer,
    "text-body-2"
  );
  return (
    <div className={assessedTextContainerClass} style={style}>
      <span>{t("common:criterion_cannot_be_assessed_text")}</span>
      <InformationOutlined size={"xxx-small"} variant={"subtle"} />
    </div>
  );
};

//This function returns the box based on the header and typeOFEvaluation
export const MarksBox = props => {
  const {
    isParentNode = false,
    isRootNode = false,
    criteriaLabel = "",
    criterionIds = [],
    criteria,
    t,
    ratedCriteria,
    typeOfScale,
    isEditable = true,
    boxLabelLocale = "common:score_with_label_and_score",
    showCriterionSelection,
    isCriteriaNotSelected = false,
    showRevampedUI = false,
  } = props;

  const { dsVersion } = useDSContext();
  const isDSV2 = dsVersion === "2.0";

  const totalScore =
    showCriterionSelection && _.isEmpty(criterionIds)
      ? "--"
      : getTotalScoreMemoized({ criterionIds, criteria });

  let boxLabel = t("common:maximum_marks_with_score", { score: totalScore });

  const { value: totalRatedScore, showDash } = getRatedScoreMemoized({
    criterionIds,
    ratedCriteria,
  });

  if (typeOfScale == TYPE_EVALUATION) {
    const scoreText = showDash
      ? showRevampedUI
        ? "-"
        : "--"
      : `${totalRatedScore}/${totalScore}`;

    let label = t("common:total");

    if (isEditable) {
      label = criteriaLabel ? criteriaLabel : t("common:predicted");
    }

    boxLabel = t(boxLabelLocale, {
      score: scoreText,
      label,
    });
  }

  const boxClass = classNames(
    {
      [classes.headerMarksBox]: !showRevampedUI,
    },
    { "heading-6": true },
    {
      [classes.unMarkedBox]:
        (_.isEqual(typeOfScale, TYPE_UNMARKED) || showDash) && !showRevampedUI,
    },
    { [classes.criteriaBox]: !isParentNode },
    { [classes.disabledNode]: isCriteriaNotSelected },
    { "text-body-2": isParentNode && isRootNode },
    { "text-body-3": isParentNode && !isRootNode },
    { "text-body-3": !isParentNode && !isRootNode }
  );

  if (isDSV2) {
    return (
      <Tag color={isRootNode && isParentNode ? "violet" : "neutral"}>
        {boxLabel}
      </Tag>
    );
  }

  return <div className={boxClass}>{boxLabel}</div>;
};

const ExpandedContent = props => {
  const {
    leafNode,
    showDescriptorTableTitle = true,
    t,
    projectGroupType = "",
    firstColumnTitle,
    secondColumnTitle,
    showRevampedUI,
    flatListedAssessmentTool,
    isDescriptorV2,
  } = props;

  const { criteriaType } = leafNode;
  const leafNodeObjectiveRubricDescriptors = _.get(
    leafNode,
    "objectiveRubricDescriptors",
    []
  );

  const isCoreProject = getIsCoreOrSubjectGroupType({ projectGroupType });
  const isNewDisplaySequence =
    isCoreProject &&
    checkIfDisplaySequenceIsFlipped({
      categories: leafNodeObjectiveRubricDescriptors,
      labelPath: "achievementLevel.label",
    });

  const updatedLeafNodeObjectiveRubricDescriptors = useMemo(
    () =>
      _.orderBy(
        leafNodeObjectiveRubricDescriptors,
        ["displaySequence"],
        isNewDisplaySequence ? ["desc"] : ["asc"]
      ),
    [isNewDisplaySequence]
  );

  const objectiveRubricDescriptors = _.map(
    updatedLeafNodeObjectiveRubricDescriptors,
    item => {
      return {
        achievementLevel: _.get(item, "achievementLevel.label", ""),
        descriptor: _.get(item, "label", ""),
        subText: _.get(item, "achievementLevel.subText", ""),
      };
    }
  );

  const descriptorTableDescriptionClass = classNames(
    classes.descriptorTableDescription,
    "text-body"
  );
  const descriptorTableTitleClass = classNames(
    classes.descriptorTableTitle,
    "text-label-3"
  );

  const handleKeyDown = e => {
    if (_.includes(["ArrowDown", "ArrowUp"], e.key)) {
      e.stopPropagation();
    }
  };

  return (
    <div
      className={classes.descriptorTableContainer}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {leafNode?.subText && (
        <div
          className={descriptorTableDescriptionClass}
          dangerouslySetInnerHTML={{
            __html: leafNode?.subText,
          }}
        />
      )}
      {showDescriptorTableTitle ? (
        <div className={descriptorTableTitleClass}>
          {t("common:descriptor_table_label")}
        </div>
      ) : null}
      {isDescriptorV2 && criteriaType !== CRITERIA_RUBRIC_FINAL_GRADE ? (
        <DescriptorTableV2
          flatListedAssessmentTool={flatListedAssessmentTool}
          criteriaId={_.get(leafNode, "id", "")}
          t={t}
        />
      ) : (
        <DescriptorTable
          rowData={objectiveRubricDescriptors}
          firstColumnTitle={firstColumnTitle ?? t("common:mark")}
          secondColumnTitle={secondColumnTitle ?? t("common:level_descriptor")}
          isSortingEnabled={false}
          dsVersion="2.0"
          showRevampedUI={showRevampedUI}
        />
      )}
    </div>
  );
};

//This is the component for Leaf nodes Ex : Criterion 1, etc
const LeafNodes = props => {
  const {
    leafNodes,
    t,
    criteria,
    typeOfScale,
    assessmentHeading,
    customLabelClass,
    rightContainerWidth,
    leftContainerWidth,
    singleLevel,
    showGradeComputationLoader,
    toggleRubricDescriptorModal,
    showCommentForRating,
    showModalUIForDescriptors,
    projectId,
    projectGroupType,
    assessmentLeafNodLabelStyles,
    assessmentInformationIconStyles,
    testId,
    showInfoIcon,
    isAiEvaluationEnabled,
    isGeneratingAIResponse,
    isAISelectedCriteria,
    customSelectedRatingElement,
    isDescriptorV2,
    isSelectionModalMode = false,
    ...rest
  } = props;

  const {
    showCriterionSelection,
    getIsCriterionSelected,
    onClickCheckbox,
    showAssessmentLabelAsRootNode,
    leafNodeConStyle = {},
    ibPYPElementRemarks,
    onUpdateCriteriaComment,
    isEditable,
    headerLabel,
    activeComment,
    commentInputRef,
    titleComponentContainerStyle,
    leafNodLabelStyles,
    accordianHeaderStyle,
    showRevampedUI,
  } = rest;

  const { dsVersion } = useDSContext();
  const isDSV2 = dsVersion === "2.0";

  const [expandedNodes, setExpandedNodes] = useState({});
  const onAccordionExpand = useCallback((leafNodeId, wasExpanded) => {
    setExpandedNodes(prev => ({ ...prev, [leafNodeId]: !wasExpanded }));
  }, []);

  //This function is used to return right container of custom Title Component of Criterion
  //which are CannotBeAssessedTextComp when it is not assessed
  //          marks box for "unMarked" evaluation Type
  //          Criteria Rating for "evaluationScale"
  const getRightComponent = ({ leafNode, rightContainerWidth }) => {
    const canBeAssessed = _.get(leafNode, "canBeAssessed", true);
    const isCriterionSelected = showCriterionSelection
      ? getIsCriterionSelected?.(leafNode?.id)
      : true;

    const ibPYPElementId =
      leafNode?.academicCriteriaSetCriteriaType && leafNode?.ibPYPElementId
        ? leafNode?.ibPYPElementId
        : leafNode?.id;
    const criteriaRemarks = _.find(ibPYPElementRemarks, {
      ibPYPElementId,
    });

    const handleUpdateCriteriaComment = value => {
      onUpdateCriteriaComment({
        ibPYPElementId,
        remarkMessage: value,
      });
    };

    return canBeAssessed ? (
      _.isEqual(typeOfScale, TYPE_UNMARKED) ? (
        <MarksBox
          criterionIds={[leafNode?.id]}
          criteria={criteria}
          t={t}
          typeOfScale={typeOfScale}
          showCriterionSelection={showCriterionSelection}
          isCriteriaNotSelected={!isCriterionSelected}
          {...rest}
        />
      ) : (
        <div className={classes.ratingWithCommentsContainer}>
          {!isGeneratingAIResponse ? (
            <CriteriaRatingV2
              highestMark={_.get(leafNode, "highestMark", 0)}
              highestGrade={_.get(leafNode, "highestGrade", undefined)}
              id={leafNode?.id}
              type={leafNode.type}
              width={!isAiEvaluationEnabled ? rightContainerWidth : null}
              leafNode={leafNode}
              showGradeComputationLoader={showGradeComputationLoader}
              selectedCriteriaRef={
                !_.includes(activeComment, "criterion_comment") &&
                (_.includes(activeComment, ibPYPElementId) ||
                  _.includes(activeComment, "grade"))
                  ? commentInputRef
                  : undefined
              }
              customSelectedRatingElement={
                isAISelectedCriteria
                  ? classes.customAISelectedRatingElement
                  : customSelectedRatingElement
              }
              {...rest}
            />
          ) : null}

          {isGeneratingAIResponse ? (
            <div className={classes.skeletonContainer}>
              {_.times(_.get(leafNode, "highestMark", 0), index => {
                return <SkeletonLoader key={index} width={44} height={32} />;
              })}
            </div>
          ) : null}

          {showCommentForRating ? (
            <CriteriaComment
              key={projectId}
              criteriaRemarks={criteriaRemarks}
              updateComment={handleUpdateCriteriaComment}
              disabled={!isEditable}
              inputRef={
                _.includes(activeComment, "criterion_comment") &&
                _.includes(activeComment, ibPYPElementId)
                  ? commentInputRef
                  : undefined
              }
              testId={`${TEST_ID_PREFIX}-${leafNode?.id}`}
            />
          ) : null}
        </div>
      )
    ) : (
      <CannotBeAssessedTextComp
        t={t}
        width={rightContainerWidth}
        typeOfScale={typeOfScale}
      />
    );
  };

  const getTitleComponent = ({
    leafNode,
    assessmentHeading,
    rightContainerWidth,
    leftContainerWidth,
    showCaret = false,
    isExpanded = false,
  }) => {
    const areNoCriteriaSelected = showCriterionSelection
      ? !getIsCriterionSelected?.(leafNode?.id)
      : false;
    const leafNodeLabelClass = classNames(
      customLabelClass ? customLabelClass : classes.leafNodeLabel,

      {
        [classes.disabledNode]: areNoCriteriaSelected,
        ["text-label-1"]: !isDSV2 && !isAiEvaluationEnabled,
        "text-body": isDSV2 && isSelectionModalMode,
        "text-label": isDSV2 && !isSelectionModalMode,
        ["text-label-l"]: isAiEvaluationEnabled,
      }
    );

    const oldSubtitleClasses = classNames("text-body-2", classes.subtleText);
    const newSubtitleClasses = classNames("text-body", classes.textSecondary);

    const subtitleClasses = classNames({
      [oldSubtitleClasses]: !isDSV2,
      [newSubtitleClasses]: isDSV2,
    });

    const label = leafNode?.label ? leafNode?.label : assessmentHeading;

    const leafNodeHeaderClasses = classNames({
      [classes.leafNodeHeader]: true,
      [classes.leafNodeHeaderWithComments]: showModalUIForDescriptors,
    });

    const handleClick = () => {
      toggleRubricDescriptorModal({ leafNode });
    };

    const getInfoButton = (styles = {}) => (
      <IconButton
        icon={<InformationOutlined size="xxx-small" variant="subtle" />}
        isCompact={true}
        size="small"
        onClick={handleClick}
        variant="neutral"
        type="plain"
        _legacy_variant="plain"
        className={classes.infoIcon}
        style={{ ...assessmentInformationIconStyles, ...styles }}
      />
    );

    return showModalUIForDescriptors ? (
      <div className={leafNodeHeaderClasses}>
        <div className={isDSV2 ? null : classes.headerWithIconContainer}>
          <div className={classes.headerWithIconInnerContainer}>
            <div
              className={leafNodeLabelClass}
              style={assessmentLeafNodLabelStyles}
            >
              {label}
              {isDSV2 && showInfoIcon
                ? getInfoButton(styles.inlineInfoIconStyles)
                : null}
            </div>
            {!isDSV2 && showInfoIcon ? getInfoButton() : null}
          </div>
        </div>
        {getRightComponent({
          leafNode,
          rightContainerWidth,
        })}
      </div>
    ) : showCaret ? (
      <div className={classes.leafNodeHeader}>
        <div className={classes.labelWithCaret}>
          <div
            className={leafNodeLabelClass}
            style={{
              ...leafNodLabelStyles,
              ...(isDSV2 ? styles.leftContainerStyle : {}),
            }}
          >
            {label}
          </div>
          {isExpanded ? (
            <CaretOpenedFilled
              size="xx-small"
              variant="subtle"
              className={classes.caretIcon}
            />
          ) : (
            <CaretClosedFilled
              size="xx-small"
              variant="subtle"
              className={classes.caretIcon}
            />
          )}
        </div>
        <div className={classes.scoreTagContainer}>
          {getRightComponent({
            leafNode,
            rightContainerWidth,
          })}
        </div>
      </div>
    ) : (
      <div className={classes.leafNodeHeader}>
        <div
          className={leafNodeLabelClass}
          style={{
            width: leftContainerWidth,
            ...leafNodLabelStyles,
            ...(isDSV2 ? styles.leftContainerStyle : {}),
          }}
        >
          {label}
        </div>
        {getRightComponent({
          leafNode,
          rightContainerWidth,
        })}
      </div>
    );
  };

  const leafNodeCheckboxConClass = classNames({
    [classes.leafNodeCheckboxConWithoutParent]:
      showCriterionSelection && singleLevel,
    [classes.leafNodeCheckboxCon]: showCriterionSelection && !singleLevel,
    [classes.leafNodeCon]:
      !showCriterionSelection && showAssessmentLabelAsRootNode,
  });

  const titleComponentContainerClasses = classNames({
    [classes.titleComponentContainer]: true,
    [classes.titleComponentContainerV2]: isDSV2,
  });

  return _.map(leafNodes, leafNode => {
    return (
      <div className={leafNodeCheckboxConClass} style={leafNodeConStyle}>
        {showCriterionSelection ? (
          <Checkbox
            isChecked={getIsCriterionSelected?.(leafNode.id)}
            onClick={() => onClickCheckbox?.(leafNode.id)}
            style={styles.checkboxStyle}
          />
        ) : null}
        {showModalUIForDescriptors ? (
          <div
            className={titleComponentContainerClasses}
            style={titleComponentContainerStyle}
          >
            {getTitleComponent({
              leafNode,
              assessmentHeading,
              rightContainerWidth,
              leftContainerWidth,
            })}
          </div>
        ) : (
          <Accordian
            showLeftArrow={!isSelectionModalMode}
            headerStyle={{
              ...(isDSV2 ? styles.headerStyleV2 : styles.headerStyle),
              ...accordianHeaderStyle,
            }}
            customRightArrow={isSelectionModalMode ? <div></div> : null}
            mainContentContainerStyle={styles.accordianContainerStyle}
            containerStyle={isDSV2 ? styles.containerStyle : {}}
            getIsExpanded={isExp => onAccordionExpand(leafNode?.id, isExp)}
            customTitleComponent={getTitleComponent({
              leafNode,
              assessmentHeading,
              rightContainerWidth,
              leftContainerWidth,
              showCaret: isSelectionModalMode,
              isExpanded: expandedNodes[leafNode?.id] ?? false,
            })}
            expandedContent={
              <ExpandedContent
                leafNode={leafNode}
                t={t}
                projectGroupType={projectGroupType}
                showRevampedUI={showRevampedUI}
              />
            }
            showLabelBorder={
              showCriterionSelection || isAiEvaluationEnabled ? false : true
            }
            testId={`${testId}-${leafNode?.id}`}
            dsVersion={dsVersion}
          />
        )}
      </div>
    );
  });
};

const BodyContainerComp = props => {
  const {
    t,
    criteria,
    showGradeComputationLoader,
    selectedCriteria,
    toggleRubricDescriptorModal,
    projectGroupType,
    ibPYPElementRatings,
    isAiEvaluationEnabled,
    isGeneratingAIResponse,
    isDescriptorV2,
    userType,
    isSelectionModalMode = false,
    ...rest
  } = props;

  const {
    showCriterionSelection,
    getCheckboxState,
    onClickCheckbox,
    showAssessmentLabelAsRootNode,
    getIsCriterionSelected,
    showCommentForRating,
    ibPYPElementRemarks,
    onUpdateCriteriaComment,
    activeComment,
    commentInputRef,
    headerLabel,
    showModalUIForDescriptors,
  } = rest;

  const isStaff = userType === "staff";

  const nestedCriteria = getCriteriaNestedMemoized({ criteria });

  const { dsVersion } = useDSContext();
  const isDSV2 = dsVersion === "2.0";

  const singleLevel = !_.some(criteria, item => !_.isEqual(item.parent, null));
  const parentNodeLabelClass = classNames(classes.headerLabel, {
    ["text-label-1"]: !isDSV2,
    "text-body": isDSV2 && isSelectionModalMode,
    ["text-label-l"]: isDSV2 && !isSelectionModalMode,
  });
  const isThereALeafNodeInBetween = _.some(criteria, (item, idx) => {
    return _.isEmpty(_.get(item, "children", [])) && idx !== 0;
  });

  return _.map(nestedCriteria, (criteriaLevel, index) => {
    const areChildrenEmpty = _.isEmpty(criteriaLevel["children"]);

    const isLastCriteriaLevel = index === nestedCriteria?.length - 1;

    const aiIBPYPElementRating = _.find(ibPYPElementRatings, {
      ibPYPElementId: criteriaLevel?.id,
      creatorType: "AI",
    });

    const justificationMessage = _.get(
      aiIBPYPElementRating,
      "evaluationJustification.message",
      ""
    );

    const maxRating = _.get(criteriaLevel, "highestMark", 0);
    const aiSelectedCriteria = _.get(aiIBPYPElementRating, "value", "");

    const aiEvaluationLabel =
      aiSelectedCriteria && `${aiSelectedCriteria}/${maxRating}`;

    const isAISelectedCriteria =
      !_.isEmpty(aiIBPYPElementRating) && !aiIBPYPElementRating?.isDeleted;

    if (areChildrenEmpty || isDescriptorV2) {
      const isRootLevelLeaf = _.isNil(criteriaLevel.parent);
      const getLeafClassName = () => {
        if (isThereALeafNodeInBetween) {
          if (showAssessmentLabelAsRootNode && isRootLevelLeaf) {
            return isSelectionModalMode ? null : classes.parentNode;
          }
          return classes.reducedTopMargin;
        }
        return singleLevel
          ? classes.singleLevelBodyContainer
          : classes.noRootNoChildCriteriaClass;
      };
      return (
        <div
          className={getLeafClassName()}
          style={
            isAiEvaluationEnabled && !isLastCriteriaLevel
              ? styles.toolWithJustification
              : null
          }
        >
          <LeafNodes
            leafNodes={[criteriaLevel]}
            t={t}
            criteria={criteria}
            {...rest}
            customLabelClass={!singleLevel ? parentNodeLabelClass : null}
            singleLevel={singleLevel}
            showGradeComputationLoader={showGradeComputationLoader}
            toggleRubricDescriptorModal={toggleRubricDescriptorModal}
            showCommentForRating={showCommentForRating}
            showModalUIForDescriptors={showModalUIForDescriptors}
            ibPYPElementRemarks={ibPYPElementRemarks}
            onUpdateCriteriaComment={onUpdateCriteriaComment}
            headerLabel={headerLabel}
            activeComment={activeComment}
            commentInputRef={commentInputRef}
            projectGroupType={projectGroupType}
            isDescriptorV2={isDescriptorV2}
            isAiEvaluationEnabled={isAiEvaluationEnabled}
            isGeneratingAIResponse={isGeneratingAIResponse}
            isAISelectedCriteria={isAISelectedCriteria}
            isSelectionModalMode={isSelectionModalMode}
          />

          {isStaff &&
          isAiEvaluationEnabled &&
          !_.isEmpty(justificationMessage) ? (
            <AIJustification
              justifications={[justificationMessage]}
              isAIGeneratingResponse={isGeneratingAIResponse}
              aiEvaluationLabel={aiEvaluationLabel}
            />
          ) : null}
        </div>
      );
    } else {
      const leafNodes = _.filter(
        _.get(criteriaLevel, "children", []),
        leafNode => _.get(leafNode, "id")
      );
      const leafNodeIds = _.map(leafNodes, item => item.id);

      const criterionIds = !showCriterionSelection
        ? leafNodeIds
        : _.intersection(selectedCriteria, leafNodeIds);

      const areNoCriteriaSelected = showCriterionSelection
        ? !_.some(criterionIds, criterionId =>
            getIsCriterionSelected?.(criterionId)
          )
        : false;

      const checkboxState = getCheckboxState?.(criteriaLevel.id);

      const parentNodeClass = classNames(classes.parentNode, {
        [classes.parentNodeNested]:
          showAssessmentLabelAsRootNode && !isThereALeafNodeInBetween,
        [classes.parentNodeIndented]:
          showAssessmentLabelAsRootNode && isThereALeafNodeInBetween,
      });

      const labelClass = classNames(parentNodeLabelClass, {
        [classes.disabledNode]: areNoCriteriaSelected,
      });

      const parentNodeHeaderClass = classNames(classes.parentNodeHeader, {
        [classes.parentNodeHeaderV2]: isDSV2,
      });

      return (
        <div key={criteriaLevel.id} className={parentNodeClass}>
          <div className={parentNodeHeaderClass}>
            <div className={classes.parentNodeCheckboxCon}>
              {showCriterionSelection ? (
                <Checkbox
                  isIndeterminate={checkboxState?.isIndeterminate}
                  isChecked={checkboxState?.isChecked}
                  onClick={() => onClickCheckbox?.(criteriaLevel.id)}
                />
              ) : null}
              <div className={labelClass}>{criteriaLevel.label}</div>
            </div>

            <MarksBox
              criterionIds={criterionIds}
              criteria={criteria}
              t={t}
              {...rest}
              isParentNode={true}
              isCriteriaNotSelected={areNoCriteriaSelected}
            />
          </div>
          <LeafNodes
            leafNodes={leafNodes}
            t={t}
            criteria={criteria}
            {...rest}
            singleLevel={singleLevel}
            toggleRubricDescriptorModal={toggleRubricDescriptorModal}
            showCommentForRating={showCommentForRating}
            showModalUIForDescriptors={showModalUIForDescriptors}
            headerLabel={headerLabel}
            isGeneratingAIResponse={isGeneratingAIResponse}
            isDescriptorV2={isDescriptorV2}
            isSelectionModalMode={isSelectionModalMode}
          />
        </div>
      );
    }
  });
};

const RootNode = props => {
  const {
    criteriaData,
    assessmentTool,
    getIsCriterionSelected,
    updateSelectedCriteria,
    showCriterionSelection,
    updateFooterDetails,
    areNoCriteriaSelected,
    isSelectionModalMode = false,
    hideLabel = false,
  } = props;

  const { t } = useI18n(["classRoom"]);
  const label = _.get(assessmentTool, "label", "");
  const rootNodeClass = classNames(classes.rootNodeContainer, {
    "text-label-1": !isSelectionModalMode,
    "text-label": isSelectionModalMode,
    [classes.disabledNode]: areNoCriteriaSelected,
  });
  const criteria = _.flatten(
    _.map(criteriaData, item => _.get(item, "criteria", []))
  );
  const areAllCriterionSelected = !_.some(
    criteria,
    criterion => !getIsCriterionSelected?.(_.get(criterion, "id", ""))
  );
  const areSomeCriterionSelected =
    _.some(criteria, criterion =>
      getIsCriterionSelected?.(_.get(criterion, "id", ""))
    ) &&
    _.some(
      criteria,
      criterion => !getIsCriterionSelected?.(_.get(criterion, "id", ""))
    );
  const onClickLabelCheckbox = () => {
    if (areAllCriterionSelected) {
      updateSelectedCriteria([]);
      updateFooterDetails({
        disableButton3: true,
        button3DisabledTooltip: t(
          "classRoom:select_atleast_one_criterion_to_tag_rubric"
        ),
      });
    } else {
      updateSelectedCriteria(
        _.map(criteria, criterion => _.get(criterion, "id"))
      );
      updateFooterDetails({
        disableButton3: false,
        button3DisabledTooltip: "",
      });
    }
  };

  return (
    <div className={rootNodeClass}>
      {showCriterionSelection ? (
        <Checkbox
          isChecked={areAllCriterionSelected}
          isIndeterminate={areSomeCriterionSelected}
          onClick={onClickLabelCheckbox}
        />
      ) : null}
      {!hideLabel ? label : null}
    </div>
  );
};

const CriteriaRubricAssessment = props => {
  const {
    containerStyle,
    typeOfScale,
    projectGroupType,
    criteriaData,
    ibPYPElementRatings,
    isAiEvaluationEnabled,
    isEvaluated,
    onAcceptAIEvaluation,
    academicTermId,
    isGeneratingAIResponse,
    userType,
    flatListedAssessmentTool,
    isDescriptorV2,
    ...rest
  } = props;

  const isAIEvaluationExist = _.some(ibPYPElementRatings, {
    creatorType: "AI",
  });

  const isAIEvaluatedTool = _.some(ibPYPElementRatings, {
    creatorType: "AI",
    isDeleted: false,
  });

  const [
    rubricDescriptorModalDetails,
    setRubricDescriptorModalDetails,
  ] = useState({ showModal: false });
  const toggleRubricDescriptorModal = ({ leafNode } = {}) => {
    if (rubricDescriptorModalDetails?.showModal)
      setRubricDescriptorModalDetails({
        showModal: false,
        leafNode: undefined,
      });
    else
      setRubricDescriptorModalDetails({
        showModal: true,
        leafNode,
      });
  };

  const {
    selectedCriteria,
    showCriterionSelection,
    assessmentTool,
    getIsCriterionSelected,
    updateSelectedCriteria,
    showAssessmentLabelAsRootNode,
    updateFooterDetails,
    customHeaderLabelClass,
    headerStyle,
    assessmentHeading,
    sourceFrom,
    showToolIcon = false,
    marksLabelLocale,
    showRevampedUI,
    isSelectionModalMode = false,
    hideRootNodeLabel = false,
  } = rest;

  const { dsVersion } = useDSContext();
  const isDSV2 = dsVersion === "2.0";
  const { t } = useI18n(["common"]);
  const maxMarks = _.max(
    _.flatten(
      _.map(criteriaData, item =>
        _.map(_.get(item, "criteria", []), eachCriteria =>
          _.get(eachCriteria, "highestMark", 0)
        )
      )
    )
  );
  const { isAssessmentToolEditMode } = rest;
  useEffect(() => {
    const {
      isAssessmentToolEditMode,
      assessmentTool,
      updateSelectedCriteria,
    } = rest;
    if (isAssessmentToolEditMode) {
      updateSelectedCriteria?.(_.map(_.get(assessmentTool, "criteria"), "id"));
    }
  }, [isAssessmentToolEditMode]);

  const maxWidthForRatings = (maxMarks + 2) * RATING_ELEMENT_WIDTH;

  const maxWidthForLeftContainer = !_.isEqual(typeOfScale, TYPE_UNMARKED)
    ? `calc(100% - ${maxWidthForRatings + 24}px)`
    : `calc(100% - 160px)`;

  const boxLabelLocale = !_.isEmpty(marksLabelLocale)
    ? marksLabelLocale
    : "common:score_with_label_and_score";
  const headerLabelClass = classNames({
    [classes.headerLabel]: true,
    [customHeaderLabelClass]: !_.isEmpty(customHeaderLabelClass),
    ["heading-5"]: !isDSV2 && !isAiEvaluationEnabled,
    "text-label-l": isAiEvaluationEnabled,
  });

  const headerSubTextClass = classNames({
    [classes.headerSubText]: !isDSV2,
    "text-body-2": !isDSV2,
    "text-body": isDSV2,
    [classes.textSecondary]: isDSV2,
  });

  const areCriteriaNested = _.some(
    _.flatten(_.map(criteriaData, item => _.get(item, "criteria", []))),
    criterion =>
      !_.isEmpty(_.get(criterion, "children", [])) &&
      criterion.type !== "DP_CRITERIA"
  );

  const showAIActions =
    isAiEvaluationEnabled &&
    !isEvaluated &&
    isAIEvaluationExist &&
    !isGeneratingAIResponse;

  return (
    <div className={classes.container} style={containerStyle}>
      {_.map(criteriaData, eachCriteria => {
        const {
          criteria,
          headerLabel,
          headerSubText,
          hasMarksBox,
          ratingType,
          helpUID,
          tags,
          showGradeComputationLoader = false,
          showCommentForRating,
          showModalUIForDescriptors,
          showInfoIcon = true,
        } = eachCriteria;

        const headerClass = classNames({
          [classes.multiLevelHeader]: areCriteriaNested,
          [classes.singleLevelHeader]:
            !areCriteriaNested && (headerLabel || sourceFrom === "CLASSROOM"),
        });
        const criterionIds = showCriterionSelection
          ? selectedCriteria
          : _.map(
              _.filter(criteria, item => isDescriptorV2 || !item?.children),
              param => param.id
            );

        const areNoCriteriaSelected = showCriterionSelection
          ? !_.some(criterionIds, criterionId =>
              getIsCriterionSelected?.(criterionId)
            )
          : false;

        const innerContainerClasses = classNames({
          [classes.innerContainer]: !showModalUIForDescriptors,
        });

        const renderMarksBox = () => {
          if (!hasMarksBox) return null;

          return (
            <ConditionalWrapper
              type="wrapperAsElement"
              condition={isAiEvaluationEnabled}
              wrapper={<div className={classes.marksBoxContainer} />}
            >
              <MarksBox
                {...rest}
                t={t}
                isRootNode={true}
                isParentNode={true}
                criterionIds={criterionIds}
                boxLabelLocale={boxLabelLocale}
                typeOfScale={typeOfScale}
                criteria={criteria}
                isCriteriaNotSelected={areNoCriteriaSelected}
              />
            </ConditionalWrapper>
          );
        };

        const {
          firstColumnTitle = null,
          secondColumnTitle = null,
          title = null,
        } = getDescriptorModalDetails({
          projectGroupType,
          t,
          isFinalGrade: _.get(
            rubricDescriptorModalDetails,
            "leafNode.isFinalGrade",
            false
          ),
        });

        return (
          <div className={innerContainerClasses}>
            <div className={headerClass} style={headerLabel ? headerStyle : {}}>
              <div className={isDSV2 ? null : classes.headerLabelContainer}>
                <div className={classes.headerLeftContainer}>
                  <div className={classes.headerLabelAndTagCon}>
                    <div className={classes.labelWithIcon}>
                      {showToolIcon ? (
                        <TableOutlined variant={"subtle"} size={"xxx-small"} />
                      ) : null}
                      <h6 className={headerLabelClass}>{headerLabel}</h6>
                    </div>
                    {!_.isEmpty(tags)
                      ? _.map(tags, tag => {
                          return (
                            <Tag
                              key={tag?.label}
                              size={"xx-small"}
                              color={_.get(
                                GENERIC_TAGS_INFO,
                                `${tag?.label}.color`,
                                "teal"
                              )}
                            >
                              {tag?.label}
                            </Tag>
                          );
                        })
                      : null}
                  </div>

                  {isAiEvaluationEnabled ? renderMarksBox() : null}
                </div>
                {showAssessmentLabelAsRootNode ? (
                  <RootNode
                    getIsCriterionSelected={getIsCriterionSelected}
                    updateSelectedCriteria={updateSelectedCriteria}
                    assessmentTool={assessmentTool}
                    criteriaData={criteriaData}
                    showCriterionSelection={showCriterionSelection}
                    updateFooterDetails={updateFooterDetails}
                    areNoCriteriaSelected={areNoCriteriaSelected}
                    isSelectionModalMode={isSelectionModalMode}
                    hideLabel={hideRootNodeLabel}
                  />
                ) : null}
                {!!headerSubText && (
                  <div className={headerSubTextClass}>{headerSubText}</div>
                )}
              </div>
              <div className={classes.rightHeaderContainer}>
                {!isAiEvaluationEnabled ? renderMarksBox() : null}

                {showAIActions ? (
                  <div className={classes.inlineEvaluationIconContainer}>
                    {isAIEvaluatedTool ? (
                      <Tooltip
                        tooltip={t("ai:approve_ai_evaluation", {
                          label: t("common:dp_assessment_criteria"),
                        })}
                        dsVersion="2.0"
                      >
                        <IconButton
                          icon={<TickCircleOpenOutlined />}
                          dsVersion="2.0"
                          variant="neutral"
                          type="plain"
                          size="medium"
                          disabled={!isAIEvaluatedTool}
                          onClick={() =>
                            onAcceptAIEvaluation({
                              isAcceptAllForTool: true,
                              academicTermId,
                            })
                          }
                        />
                      </Tooltip>
                    ) : null}
                  </div>
                ) : null}

                {helpUID && <HelpButton uid={helpUID} />}
              </div>
            </div>
            <BodyContainerComp
              t={t}
              {...rest}
              criteria={criteria}
              leftContainerWidth={maxWidthForLeftContainer}
              rightContainerWidth={maxWidthForRatings}
              boxLabelLocale={boxLabelLocale}
              ratingType={ratingType}
              typeOfScale={typeOfScale}
              showGradeComputationLoader={showGradeComputationLoader}
              toggleRubricDescriptorModal={toggleRubricDescriptorModal}
              showCommentForRating={showCommentForRating}
              showModalUIForDescriptors={showModalUIForDescriptors}
              headerLabel={headerLabel}
              ibPYPElementRatings={ibPYPElementRatings}
              projectGroupType={projectGroupType}
              showInfoIcon={showInfoIcon}
              userType={userType}
              isAiEvaluationEnabled={isAiEvaluationEnabled}
              isGeneratingAIResponse={isGeneratingAIResponse}
              isDescriptorV2={isDescriptorV2}
            />
            {rubricDescriptorModalDetails?.showModal ? (
              <Modal
                shouldCloseOnOverlayClick={true}
                shouldCloseOnEsc={true}
                onClose={toggleRubricDescriptorModal}
                height="700px"
                width="900px"
                dsVersion="2.0"
              >
                <Modal.Header
                  isClosable
                  title={
                    title ??
                    rubricDescriptorModalDetails?.leafNode?.label ??
                    assessmentHeading
                  }
                  hasSeparator
                  testId={`${TEST_ID_PREFIX}-${rubricDescriptorModalDetails?.leafNode?.id}`}
                />
                <Modal.Body isScrollable style={styles.modalBodyStyle}>
                  <ExpandedContent
                    leafNode={rubricDescriptorModalDetails?.leafNode}
                    t={t}
                    showDescriptorTableTitle={false}
                    projectGroupType={projectGroupType}
                    firstColumnTitle={firstColumnTitle}
                    secondColumnTitle={secondColumnTitle}
                    showRevampedUI={showRevampedUI}
                    flatListedAssessmentTool={flatListedAssessmentTool}
                    isDescriptorV2={isDescriptorV2}
                  />
                </Modal.Body>
              </Modal>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

CriteriaRubricAssessment.defaultProps = {
  //Two types : [TYPE_UNMARKED, TYPE_EVALUATION]
  typeOfScale: TYPE_UNMARKED,
  isEditable: true,
  headerLabel: "",
  headerSubText: "",
  /** Array of  [{
    ibPYPElementId: 8 ( criterion id ),
    value: 5 (selected mark in that criterion),
  },
   .......] */
  ratedCriteria: [],
  // sectionUID for help Text
  onSelectMark: () => {
    return null;
  },
  containerStyle: {},
  assessmentHeading: "",
  showCriterionSelection: false,
  selectedCriteria: [],
  assessmentTool: {},
  isAssessmentToolEditMode: false,
  showAssessmentLabelAsRootNode: false,
  headerStyle: {},
  sourceFrom: "",
  assessmentInformationIconStyles: {},
  assessmentLeafNodLabelStyles: {},
  leafNodLabelStyles: {},
  accordianHeaderStyle: {},
};

CriteriaRubricAssessment.propTypes = {
  typeOfScale: PropTypes.string,
  isEditable: PropTypes.bool,
  headerLabel: PropTypes.string,
  headerSubText: PropTypes.string,
  criteria: PropTypes.array,
  ratedCriteria: PropTypes.array,
  onSelectMark: PropTypes.func,
  helpUID: PropTypes.string,
  containerStyle: PropTypes.object,
  assessmentHeading: PropTypes.string,
  showCriterionSelection: PropTypes.bool,
  selectedCriteria: PropTypes.array,
  assessmentTool: PropTypes.object,
  isAssessmentToolEditMode: PropTypes.bool,
  showAssessmentLabelAsRootNode: PropTypes.bool,
  headerStyle: PropTypes.object,
  sourceFrom: PropTypes.string,
  assessmentInformationIconStyles: PropTypes.object,
  assessmentLeafNodLabelStyles: PropTypes.object,
  leafNodLabelStyles: PropTypes.object,
  accordianHeaderStyle: PropTypes.object,
};

const mapStateToProps = (state, ownProps) => {
  const { parentId, assessmentFields } = ownProps;

  const isGeneratingAIResponse = getIsAIGeneratingResponseForTool({
    state,
    studentAssignmentId: parentId,
    assessmentFields,
  });

  return {
    isGeneratingAIResponse,
    userType: _.get(state, "login.userInfo.user_type", ""),
  };
};

export default connect(mapStateToProps, null)(CriteriaRubricAssessment);
